import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import analytics from "web/script/analytics/analytics";
import facebookEvents from "web/script/analytics/facebook-events";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import userProfiler from "web/script/modules/userprofiler";
import storage from "web/script/utils/storage";
import requester from "web/script/modules/requester";
import url from "web/script/utils/url";
import EmailCaptureForm from "web/react/components/email-capture-form/email-capture-form";
import styles from "./homepage-overlay.css";

export function ScriptTag({ jsonLD }) {
    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLD) }}
        />
    );
}
ScriptTag.propTypes = {
    jsonLD: PropTypes.object.isRequired,
};

function HomepageOverlay({
    cookiePolicyLink,
    privacyPolicyLink,
    termsAndConditionsLink,
    refreshTest,
    signupStyle,
}) {
    const appElement = useRef(null);
    const [emailAddress, setEmailAddress] = useState("");
    const [formErrors, setFormErrors] = useState(undefined);
    const [marketingEmails, setMarketingEmails] = useState(false);
    const [overlayOpen, setOverlayOpen] = useState(true);
    const [privacyPolicy, setPrivacyPolicy] = useState(false);

    useEffect(() => {
        if (
            storage.get("homepageOverlayShown") === true ||
            userProfiler.getHomepageOverlayShown() === true
        ) {
            setOverlayOpen(false);
        } else {
            analytics.event("signup_hp_wall", "open_signup_form", "open_overlay", true);
            if (!refreshTest) {
                userProfiler.setHomepageOverlayShown();
                storage.set("homepageOverlayShown", true);
            }
        }
        Modal.setAppElement(appElement.current);
    }, [refreshTest]);

    function changeEmail(event) {
        setEmailAddress(event.target.value);
    }

    function handleLegalChange(privacyPolicyChecked, marketingEmailsChecked) {
        if (privacyPolicy !== privacyPolicyChecked || marketingEmails !== marketingEmailsChecked) {
            setPrivacyPolicy(privacyPolicyChecked);
            setMarketingEmails(marketingEmailsChecked);
        }
    }

    async function onSubmit(event) {
        event.preventDefault();
        const country = environment.get("country");
        analytics.event("signup_hp_wall", "click_signup_button", "signup_button");
        const body = {
            country,
            reason: "normal_hp_wall",
            email: emailAddress,
            marketing_emails: marketingEmails,
            privacy_policy: privacyPolicy,
        };

        const parsedURL = url.parse(window.location);
        const nextParam = parsedURL.path;

        let jsonData = undefined;
        const queryParams = url
            .toQueryString({
                next: nextParam,
                email_capture: "true",
            })
            .toString();
        try {
            jsonData = await requester.post(`/account/register/?${queryParams}`, body);
        } catch (e) {
            console.error(e);
            setFormErrors({ other: [gettext("account.login.with_magic_link.error_title")] });
            return;
        }
        if (jsonData && jsonData.success) {
            analytics.tagEvent(
                analytics.TAG_NETWORKS.FACEBOOK,
                facebookEvents.REGISTRATION_COMPLETE
            );
            if (refreshTest) {
                userProfiler.setHomepageOverlayShown();
                storage.set("homepageOverlayShown", true);
            }
            window.location = jsonData.next;
        } else {
            const { errors } = jsonData.data;
            setFormErrors(errors);
        }
    }

    function onSocialClick(authProvider) {
        event.preventDefault();
        // signup_facebook, signup_google
        analytics.event("signup_hp_wall", "click_social_sign_up", `signup_${authProvider}`);

        if (privacyPolicy !== true || marketingEmails !== true) {
            setFormErrors({
                privacy_policy: [gettext("account.register.user_must_tick_both_boxes")],
            });
            return;
        }

        if (refreshTest) {
            userProfiler.setHomepageOverlayShown();
            storage.set("homepageOverlayShown", true);
        }

        // start with the current URL (including all query parameters)
        const parsedURL = url.parse(window.location);
        const path = parsedURL.path;
        const params = url.toQueryString(parsedURL.searchParams);
        const nextParam = `${path}?${params}`;
        const searchTerm = parsedURL.searchParams.get("q");
        const searchGender =
            parsedURL.searchParams.get("gender") || parsedURL.searchParams.get("meta_gender");
        const reason = parsedURL.searchParams.get("reason");
        const type = parsedURL.searchParams.get("type");
        // update the path to point to the correct auth provider

        parsedURL.path = `/social/login/${authProvider}/`;
        parsedURL.searchParams.set("next", nextParam);

        parsedURL.searchParams.set("isLogin", false);
        parsedURL.searchParams.set("marketingEmails", marketingEmails);
        parsedURL.searchParams.set("reason", reason);

        parsedURL.searchParams.set("authType", type);
        parsedURL.searchParams.set("searchQuery", searchTerm);
        parsedURL.searchParams.set("searchGender", searchGender);

        // do the redirect
        window.location = url.unparse(parsedURL);
    }

    let title = gettext("homepage.sign_up.header_shop_thousands_brands");
    let submitText = "Continue to Lyst";
    const showSocial = ["all", "social"].includes(signupStyle);
    const hideEmail = signupStyle == "social";

    const jsonLD = {
        "@context": "https://schema.org/",
        "@type": "WebPage",
        isAccessibleForFree: "False",
        hasPart: {
            "@type": "WebPageElement",
            isAccessibleForFree: "False",
            cssSelector: ".site-wrapper",
        },
    };

    const form = (
        <div className={styles.captureForm}>
            <div className={styles.headerImage}>
                <div className={styles.headerTitle}>
                    {gettext("homepage.sign_up.welcome_title")}
                </div>
            </div>
            <div className={styles.contentBox}>
                <div className={styles.header}>
                    <h3 className={styles.title}>{title}</h3>
                </div>
                <EmailCaptureForm
                    captureType={"signup_hp_wall"}
                    cookiePolicyLink={cookiePolicyLink}
                    emailAddress={emailAddress}
                    emailLabelText={gettext(
                        "account.register.return_lead.recently_viewed_items.email_label"
                    )}
                    formErrors={formErrors}
                    onEmailChange={changeEmail}
                    onLegalChange={handleLegalChange}
                    onSubmit={onSubmit}
                    privacyPolicyLink={privacyPolicyLink}
                    submitText={submitText}
                    termsAndConditionsLink={termsAndConditionsLink}
                    shortEUPolicy={true}
                    hideEmail={hideEmail}
                    showSocial={showSocial}
                    onSocialClick={onSocialClick}
                    className={styles.form}
                />
                <div className={styles.smallText}>
                    <a
                        href="/account/login/"
                        is="tracked-link"
                        event-category="signup_hp_wall"
                        event-action="click_signin_button"
                        event-label="signin_button"
                    >
                        {" "}
                        {"Already have account? Sign in"}
                    </a>
                </div>
            </div>
        </div>
    );

    return (
        <div id="homepage-email-capture-overlay" ref={appElement}>
            <Modal
                appElement={document.body}
                isOpen={overlayOpen}
                className={styles.modal}
                overlayClassName={styles.overlay}
            >
                {form}
            </Modal>
            <ScriptTag jsonLD={jsonLD} />
        </div>
    );
}

export default HomepageOverlay;

HomepageOverlay.propTypes = {
    cookiePolicyLink: PropTypes.string.isRequired,
    privacyPolicyLink: PropTypes.string.isRequired,
    termsAndConditionsLink: PropTypes.string.isRequired,
    refreshTest: PropTypes.bool,
    signupStyle: PropTypes.string.isRequired,
};
